/**
 * AlarmList.tsx — standalone overview of all alarm rules (tenant-wide).
 * Columns: name, monitor, severity, condition summary, active toggle, updated_at.
 * Channel count removed from list view (was N+1 — one GET per row).
 * Channel details visible in AlarmEditDialog where useAlarmChannels is already loaded.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Bell, Plus, Search } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { Button, Badge, Input, Skeleton, Switch } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useAlarms,
  useWatchpoints,
  useDeleteAlarm,
  useToggleAlarm,
  type Alarm,
} from '@/api/healthcheck';
import { fmtRelTime } from '../shared';
import { SeverityBadge } from './SeverityBadge';
import { AlarmEditDialog } from './AlarmEditDialog';
import { RowMenu } from '../../components/RowMenu';
import { conditionSummary } from './condition-summary';

export function AlarmList() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const { data: alarmItems = [], isLoading, isError, refetch } = useAlarms();
  const { data: wpData } = useWatchpoints(projectId, {});
  const watchpoints = wpData?.items ?? [];

  const wpById = useMemo(
    () => new Map(watchpoints.map((w) => [w.id, w])),
    [watchpoints],
  );

  // Filter to only this project's monitors
  const monitorIds = useMemo(() => new Set(watchpoints.map((w) => w.id)), [watchpoints]);
  const alarms = useMemo(
    () => alarmItems.filter((a) => monitorIds.has(a.watchpoint_id)),
    [alarmItems, monitorIds],
  );

  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filteredAlarms = q ? alarms.filter((a) => a.name.toLowerCase().includes(q)) : alarms;

  const toggleAlarm = useToggleAlarm();
  const deleteAlarm = useDeleteAlarm();
  const [pendingDelete, setPendingDelete] = useState<Alarm | null>(null);
  const [editAlarm, setEditAlarm] = useState<Alarm | null>(null);

  const columns = useMemo<ColumnDef<Alarm, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'] }}>
            {row.original.name}
          </span>
        ),
      },
      {
        id: 'monitor',
        header: 'Monitor',
        enableSorting: false,
        cell: ({ row }) => {
          const wp = wpById.get(row.original.watchpoint_id);
          return (
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              {wp?.name ?? row.original.watchpoint_id.slice(0, 8)}
              {wp && (
                <Badge tone="neutral" appearance="subtle" size="sm" style={{ marginLeft: 4 }}>
                  {wp.type}
                </Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'severity',
        header: 'Severity',
        enableSorting: false,
        cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
      },
      {
        id: 'conditions',
        header: 'Conditions',
        enableSorting: false,
        cell: ({ row }) => (
          <span
            style={{
              color: 'var(--vn-fg-muted)',
              fontSize: 'var(--vn-font-size-sm)',
              fontFamily: 'var(--vn-font-mono)',
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {conditionSummary(row.original.conditions)}
          </span>
        ),
      },
      {
        id: 'active',
        header: 'Active',
        enableSorting: false,
        cell: ({ row }) => {
          const alarm = row.original;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={alarm.is_active}
                onCheckedChange={(checked) =>
                  toggleAlarm.mutate(
                    { id: alarm.id, is_active: checked },
                    {
                      onError: (e) =>
                        show({ tone: 'danger', title: (e as Error)?.message || 'Update failed' }),
                    },
                  )
                }
              />
            </div>
          );
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            {fmtRelTime(row.original.updated_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RowMenu
            items={[
              { label: 'Edit', onSelect: () => setEditAlarm(row.original) },
              { label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wpById, toggleAlarm.isPending],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Bell size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem current>Alarms</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Alarms"
        description="Alert rules that fire when monitor conditions are met."
        actions={
          <Button
            size="sm"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`${base}/alarms/create`)}
          >
            New alarm
          </Button>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load alarms"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The monitoring service is temporarily unavailable.
        </Alert>
      ) : isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : (
        <>
          <Toolbar
            start={
              <Input
                placeholder="Search alarms…"
                startSlot={<Search />}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            }
          />
          <DataTable
            columns={columns}
            data={filteredAlarms}
            getRowId={(a) => a.id}
            emptyState={
              alarms.length === 0 ? (
                <EmptyState
                  icon={<Bell size={28} />}
                  title="No alarm rules yet"
                  description="Create your first alert rule to get notified when a monitor fails."
                  actions={
                    <Button
                      size="sm"
                      startIcon={<Plus size={14} />}
                      onClick={() => navigate(`${base}/alarms/create`)}
                    >
                      Create your first alarm
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<Bell size={28} />}
                  title="No matching alarms"
                  description="Try a different search term."
                />
              )
            }
            footer={
              filteredAlarms.length
                ? `${filteredAlarms.length} alarm${filteredAlarms.length === 1 ? '' : 's'}`
                : undefined
            }
          />
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        destructive
        title={`Delete "${pendingDelete?.name}"?`}
        description="This alarm rule will be permanently deleted. Existing incidents are not affected."
        confirmLabel="Delete"
        loading={deleteAlarm.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const alarm = pendingDelete;
          deleteAlarm.mutate(alarm.id, {
            onSuccess: () => {
              show({ tone: 'success', title: `Deleted "${alarm.name}"` });
              setPendingDelete(null);
            },
            onError: (e) => {
              show({ tone: 'danger', title: (e as Error)?.message || 'Delete failed' });
              setPendingDelete(null);
            },
          });
        }}
      />

      <AlarmEditDialog
        alarm={editAlarm}
        onClose={() => setEditAlarm(null)}
      />
    </Container>
  );
}

